/* eslint-disable react/jsx-props-no-spreading */
import React, { SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from 'shared/components/Text/Text';
import { useForm, UseFormSetError } from 'react-hook-form';
import { TFunction } from 'i18next';
import { AchVerificationResult, useVerifyAchData } from 'api/agreement';
import { perSessionAchValidateSuccessKey } from 'constants/localStorageKeys';
import TextInput from 'shared/components/inputs/TextInput/TextInput';
import colors from 'theme/colors';
import Button from 'shared/components/Button/Button';
import { datadogRum } from '@datadog/browser-rum';
import {
  AutopayCustomBankAccountContainer,
  AutopayFooter,
  AutopayStyledForm,
} from './AutopayCustomBankAccountModal.styles';

export interface IFormInput {
  account_number: string;
  routing_number: string;
}

export type AutopayCustomBankAccountModalProps = {
  agreementId: string;
  onClickSubmit: () => void;
  onSuccess: () => void;
  onClickCancel: () => void;
};

const handleVerificationError = (
  t: TFunction,
  setError: UseFormSetError<IFormInput>,
  setErrorMessage: React.Dispatch<SetStateAction<string | null>>,
  failureReason?: string,
): void => {
  switch (failureReason) {
    case AchVerificationResult.INVALID_ROUTING_NUMBER:
      setErrorMessage(t('invalidRoutingNumber'));
      break;
    case AchVerificationResult.INVALID_ACCOUNT_NUMBER:
      setError('account_number', { message: t('invalidAccountNumber') });
      // clear previous error message if there was one
      setErrorMessage(null);
      break;
    default:
      setErrorMessage(t('failValidationMessage'));
  }
};

export function AutopayCustomBankAccountModal({
  agreementId,
  onClickSubmit,
  onSuccess,
  onClickCancel,
}: AutopayCustomBankAccountModalProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'autopayModal' });
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<IFormInput>();

  const { mutateAsync: verifyAchDataAsync } = useVerifyAchData({});

  const onSubmit = async (formData: IFormInput) => {
    clearErrors();
    setErrorMessage(null);
    onClickSubmit();
    try {
      setLoading(true);
      const response = await verifyAchDataAsync({
        agreement_id: agreementId,
        account_number: formData.account_number,
        routing_number: formData.routing_number,
      });
      if (response.verification_result === AchVerificationResult.PASSED) {
        localStorage.setItem(
          perSessionAchValidateSuccessKey,
          JSON.stringify({ [agreementId]: true }),
        );
        onSuccess();
        datadogRum.addAction('ach-enrollment-complete');
      } else {
        handleVerificationError(t, setError, setErrorMessage, response.verification_result);
      }
    } catch (error: any) {
      setErrorMessage(t('errorBody2'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AutopayCustomBankAccountContainer>
      <Text as="h2">{t('addBankAccount')}</Text>
      <AutopayStyledForm>
        <TextInput
          id="ach-form-accountNumber"
          ariaLabel={t('accountNumber')}
          label={t('accountNumber')}
          hasError={Boolean(errors.account_number)}
          errorMessage={errors.account_number?.message}
          {...register('account_number', { required: t('required') })}
        />
        <TextInput
          id="ach-form-routingNumber"
          ariaLabel={t('routingNumber')}
          label={t('routingNumber')}
          hasError={Boolean(errors.routing_number)}
          errorMessage={errors.routing_number?.message}
          {...register('routing_number', { required: t('required') })}
        />
        {errorMessage && (
          <Text as="h5" color={colors.red}>
            {errorMessage}
          </Text>
        )}
      </AutopayStyledForm>
      <AutopayFooter>
        <Button label={t('cancelButton')} styleVariant="tertiary" onClick={onClickCancel} />
        <Button label={t('submitButton')} isLoading={loading} onClick={handleSubmit(onSubmit)} />
      </AutopayFooter>
    </AutopayCustomBankAccountContainer>
  );
}

export default AutopayCustomBankAccountModal;
